import { Box, Divider, Tab } from '@mui/material';
import moment from 'moment/moment';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmployeeCalendar } from '~/components/EmployeeCalendar';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { SolaForceTabs } from '~/components/SolaForceTabs';
import { AbsenceType } from '~/data/absences/types';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { useEmployeeAbsenceLimitedData } from '~/data/absences/useEmployeeAbsenceLimitedData';
import { useCompanyData } from '~/data/company';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportParams,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useVacationsReportData,
} from '~/data/reports';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { AbsencesTable } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable';
import { PeopleModuleBasicDataAccessMode } from '~/scenes/PeopleModule/components/Tabs/BasicTab';
import {
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  useReportDataset,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { useVacationsCalendarReport } from '~/scenes/PortalModules/Analytics/People/VacationCalendarReport/useVacationCalendarReport';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

import { AbsencesCalendar } from './components/AbsencesCalendar';
import { VacationBalanceData } from './components/VacationBalanceData';
import { useSxStyles } from './useStyles';

type PropsType = {
  accessMode: PeopleModuleBasicDataAccessMode;
};

export const AbsencesTab = (props: PropsType) => {
  const { accessMode } = props;

  const {
    companyData: {
      config: { fComAbsCalendarShowAll },
    },
  } = useCompanyData();

  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();

  if (
    accessMode !== PeopleModuleBasicDataAccessMode.Full &&
    hasRoleAccessOverEmployee([Roles.SUBSTITUTE_ABSENCE_MANAGER])
  ) {
    return <AbsencesTabFullAccess />;
  }

  switch (accessMode) {
    case PeopleModuleBasicDataAccessMode.Full:
      return <AbsencesTabFullAccess />;
    case PeopleModuleBasicDataAccessMode.ITAdmin:
      return fComAbsCalendarShowAll ? (
        <AbsencesTabLimitedAccess />
      ) : (
        <AbsencesTabITAdminAccess />
      );
    case PeopleModuleBasicDataAccessMode.Limited:
    default:
      return fComAbsCalendarShowAll ? <AbsencesTabLimitedAccess /> : <></>;
  }
};

export const AbsencesTabFullAccess = () => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { selectedEmployeeId, selectedEmployeeDetails } =
    useSelectedEmployeeId();
  const { absenceEntries } = useEmployeeAbsenceData({
    employeeId: selectedEmployeeId,
  });

  const [selectedTab, setSelectedTab] = useState<AbsenceType>(
    AbsenceType.SHORT,
  );

  const getTabLabel = (tab: string) => {
    switch (tab) {
      case AbsenceType.LONG:
        return t('laLongTermAbsences');
      case AbsenceType.SHORT:
        return t('laShortTermAbsences');
      default:
        return '';
    }
  };

  return (
    <>
      <SolaForceTabs
        value={Object.keys(AbsenceType).indexOf(selectedTab)}
        onChange={(_event, value) => {
          const tab = Object.keys(AbsenceType)[value];

          if (!!tab) {
            setSelectedTab(tab as AbsenceType);
          }
        }}
        background={'fade'}
        sx={sxStyles.tabsOffset}
      >
        {Object.keys(AbsenceType).map((type, index) => (
          <Tab key={index} label={getTabLabel(type)} data-cy={type} />
        ))}
      </SolaForceTabs>

      <Box sx={sxStyles.contentTabs}>
        {selectedTab === AbsenceType.SHORT && (
          <Box sx={sxStyles.activeContent}>
            <VacationBalanceData />
            <Divider sx={sxStyles.divider} />
            <AbsencesTable
              absenceType={AbsenceType.SHORT}
              employeeId={selectedEmployeeId}
            />
          </Box>
        )}
        {selectedTab === AbsenceType.LONG && (
          <Box sx={sxStyles.activeContent}>
            <AbsencesTable
              absenceType={AbsenceType.LONG}
              employeeId={selectedEmployeeId}
            />
          </Box>
        )}
        <Divider sx={sxStyles.divider} />
        <SolaForceHeading heading={t('laAbsences')} />
        <AbsencesCalendar
          absenceEntries={absenceEntries}
          employeeId={selectedEmployeeId}
          containerWidth={500}
          shouldRedirect={false}
          employeeData={selectedEmployeeDetails.employeeData}
          accessMode={'full'}
        />
      </Box>
    </>
  );
};

export const AbsencesTabLimitedAccess = () => {
  const { t } = useTranslation();
  const { selectedEmployeeId, selectedEmployeeDetails } =
    useSelectedEmployeeId();
  const { absenceEntries } = useEmployeeAbsenceLimitedData({
    employeeId: selectedEmployeeId,
  });

  return (
    <Box>
      <SolaForceHeading heading={t('laVacationsAndAbsences')} />
      <AbsencesCalendar
        absenceEntries={absenceEntries}
        employeeId={selectedEmployeeId}
        containerWidth={500}
        shouldRedirect={false}
        employeeData={selectedEmployeeDetails.employeeData}
        accessMode={'limited'}
      />
    </Box>
  );
};

export const AbsencesTabITAdminAccess = () => {
  const sxStyles = useSxStyles();

  const { selectedEmployeeId } = useSelectedEmployeeId();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const defaultReportSelection = useMemo(() => {
    return {
      reportType: {
        type: AnalyticsReportTypes.Employee,
        employeeId: selectedEmployeeId,
        scope: AnalyticsReportScope.Direct,
      },
      params: {
        startDate: moment().toDate(),
        endDate: moment().add(90, 'days').toDate(),
        empScope: AnalyticsReportEmployeeScope.AllEmployees,
      },
    };
  }, [selectedEmployeeId]);

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [AnalyticsReportTypes.Employee, AnalyticsReportTypes.Unit],
      },
    },
  });

  const { reportRange } = useMemo(() => {
    const reportRange = {
      startDate:
        reportDatasetProps.reportSelection?.params?.startDate ??
        defaultReportSelection.params.startDate,
      endDate:
        reportDatasetProps.reportSelection?.params?.endDate ??
        defaultReportSelection.params.endDate,
    };

    return {
      reportRange,
    };
  }, [reportDatasetProps, defaultReportSelection]);

  const { data, isLoading: isLoadingReportData } = useVacationsReportData(
    reportDatasetProps.reportSelection,
  );

  const employeeData = useMemo(() => {
    return data.filter(({ fEmpId }) => fEmpId === selectedEmployeeId);
  }, [data, selectedEmployeeId]);

  const { calendarProps, isLoading: isLoadingPublicHolidays } =
    useVacationsCalendarReport({
      data: employeeData,
      ...reportRange,
    });

  const { reportSelection, setReportSelection } = reportDatasetProps;
  const handleParamsChange = useCallback(
    (
      newParams: AnalyticsReportParams<{
        startDate: Date;
        endDate: Date;
        empScope: AnalyticsReportEmployeeScope;
      }>,
    ) => {
      setReportSelection({
        ...reportSelection,
        params: newParams,
      });
    },
    [setReportSelection, reportSelection],
  );

  return (
    <>
      <Box sx={sxStyles.dateRangeSelector}>
        <ReportDateRangeSelector
          startDate={reportSelection.params?.startDate}
          endDate={reportSelection.params?.endDate}
          onRangeChange={(startDate, endDate) => {
            const { params } = reportSelection;

            if (!!startDate && !!endDate && !!params?.empScope) {
              handleParamsChange({
                empScope: params.empScope,
                startDate,
                endDate,
              });
            }
          }}
          mode={ReportDateRangeSelectorMode.DateRange}
        />
      </Box>

      {isLoadingReportData || isLoadingPublicHolidays ? (
        <LoadingView />
      ) : (
        <EmployeeCalendar
          containerWidth={500}
          startDate={reportRange.startDate}
          endDate={reportRange.endDate}
          {...calendarProps}
          onEmployeeClick={goToEmployeeBasicDataView}
          hideEmployeeName={true}
        />
      )}
    </>
  );
};
